import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Megaphone, ExternalLink, Sparkles } from 'lucide-react';
import {
  getAdSessionId,
  isCapped,
  recordImpression,
  getFrequencyCap,
} from '@/lib/adSession';
import { detectVisitorCity, getCachedVisitorCity } from '@/lib/visitorCity';

interface Campaign {
  id: string;
  title: string;
  description: string | null;
  image_url: string;
  target_url: string | null;
  advertiser_name: string;
  tier: string;
  end_date: string;
  target_agency_tiers?: string[] | null;
}

interface Props {
  placement?: 'right_sidebar' | 'agencies_inline_mobile' | 'agencies_city_section';
  agencyTier?: string | null;
  variant?: 'sidebar' | 'horizontal';
  heading?: string;
  limit?: number;
  /** When true, skip frequency cap + event logging (for the admin preview). */
  previewMode?: boolean;
  page?: string;
}

// Module-level guards so the same campaign isn't logged twice across rerenders/remounts in this tab
const trackedImpressions = new Set<string>();
const trackedClicks = new Set<string>();

export function AgencyAdsSidebar({
  placement = 'right_sidebar',
  agencyTier,
  variant = 'sidebar',
  heading = 'Sponsored',
  limit = 5,
  previewMode = false,
  page = 'agencies',
}: Props) {
  const today = new Date().toISOString().slice(0, 10);
  const sessionId = useMemo(() => getAdSessionId(), []);
  const [visitorCity, setVisitorCity] = useState<string>(getCachedVisitorCity());
  useEffect(() => {
    if (previewMode || visitorCity) return;
    let alive = true;
    detectVisitorCity().then((c) => { if (alive) setVisitorCity(c); });
    return () => { alive = false; };
  }, [previewMode, visitorCity]);

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ['agency-ads', placement, today, limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ad_campaigns')
        .select('id,title,description,image_url,target_url,advertiser_name,tier,end_date,placement,pages,is_active,start_date,target_agency_tiers')
        .eq('is_active', true)
        .eq('placement', placement)
        .contains('pages', [page])
        .lte('start_date', today)
        .gte('end_date', today)
        .order('tier', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data || []) as Campaign[];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Filter by agency tier + frequency cap
  const visible = (campaigns || []).filter((c) => {
    const tiers = (c.target_agency_tiers || []) as string[];
    if (tiers.length) {
      if (!agencyTier) return false;
      if (!tiers.includes(agencyTier)) return false;
    }
    if (!previewMode && isCapped(c.id, placement)) return false;
    return true;
  });

  // Log impressions exactly once per campaign per session (and respect frequency cap)
  const idsKey = visible.map((c) => c.id).join(',');
  const lastTrackedKey = useRef<string>('');
  useEffect(() => {
    if (previewMode) return;
    if (!visible.length || lastTrackedKey.current === idsKey) return;
    lastTrackedKey.current = idsKey;
    visible.forEach((c) => {
      if (trackedImpressions.has(c.id)) return;
      trackedImpressions.add(c.id);
      recordImpression(c.id, placement);
      supabase.rpc('log_ad_event', {
        _campaign_id: c.id,
        _event_type: 'impression',
        _session_id: sessionId,
        _placement: placement,
        _page: page,
        _user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        _referrer: typeof document !== 'undefined' ? document.referrer : null,
        _city: visitorCity || null,
      } as any).then(() => {});
    });
  }, [idsKey, visible, sessionId, placement, page, previewMode, visitorCity]);

  const handleClick = (id: string) => {
    if (previewMode) return;
    if (trackedClicks.has(id)) return;
    trackedClicks.add(id);
    supabase.rpc('log_ad_event', {
      _campaign_id: id,
      _event_type: 'click',
      _session_id: sessionId,
      _placement: placement,
      _page: page,
      _user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      _referrer: typeof document !== 'undefined' ? document.referrer : null,
      _city: visitorCity || null,
    } as any).then(() => {});
  };

  if (isLoading) {
    return (
      <div className={variant === 'horizontal' ? 'flex gap-3 overflow-x-auto pb-2' : 'space-y-4'}>
        <Skeleton className="h-6 w-32" />
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className={variant === 'horizontal' ? 'h-48 w-64 rounded-xl shrink-0' : 'h-56 rounded-xl'} />
        ))}
      </div>
    );
  }

  const renderCard = (c: Campaign) => {
    const Card = (
      <div className="group rounded-xl border bg-card overflow-hidden hover:shadow-md transition-shadow h-full">
        <div className="relative aspect-[16/10] overflow-hidden bg-muted">
          <img
            src={c.image_url}
            alt={c.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <Badge className="absolute top-2 left-2 text-[10px] bg-background/90 text-foreground border-0">
            Ad
          </Badge>
          {c.tier === 'premium' && (
            <Badge className="absolute top-2 right-2 text-[10px] bg-primary text-primary-foreground border-0 gap-1">
              <Sparkles className="h-3 w-3" /> Premium
            </Badge>
          )}
        </div>
        <div className="p-3 space-y-1.5">
          <h4 className="font-semibold text-sm leading-tight line-clamp-2 group-hover:text-primary transition-colors">
            {c.title}
          </h4>
          <p className="text-[11px] text-muted-foreground line-clamp-1">by {c.advertiser_name}</p>
          {c.description && (
            <p className="text-[11px] text-muted-foreground line-clamp-2">{c.description}</p>
          )}
          {c.target_url && (
            <div className="flex items-center gap-1 text-[10px] text-primary font-semibold pt-1">
              Learn more <ExternalLink className="h-3 w-3" />
            </div>
          )}
        </div>
      </div>
    );
    return c.target_url && !previewMode ? (
      <a
        key={c.id}
        href={c.target_url}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={() => handleClick(c.id)}
        className={variant === 'horizontal' ? 'block w-64 shrink-0 snap-start' : 'block'}
      >
        {Card}
      </a>
    ) : (
      <div key={c.id} className={variant === 'horizontal' ? 'w-64 shrink-0 snap-start' : ''}>
        {Card}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Megaphone className="h-4 w-4 text-primary" />
        <h3 className="font-display font-bold text-sm">{heading}</h3>
        {previewMode && (
          <Badge variant="outline" className="text-[10px]">Preview · cap {getFrequencyCap()}/24h</Badge>
        )}
      </div>

      {visible.length > 0 ? (
        variant === 'horizontal' ? (
          <div className="flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2 -mx-4 px-4">
            {visible.map(renderCard)}
          </div>
        ) : (
          <div className="space-y-4">{visible.map(renderCard)}</div>
        )
      ) : (
        previewMode && (
          <p className="text-xs text-muted-foreground italic">
            No active campaigns match this placement & agency tier.
          </p>
        )
      )}

      {!previewMode && (
        <div className="rounded-xl border border-dashed bg-muted/40 p-4 text-center space-y-2">
          <Megaphone className="h-5 w-5 mx-auto text-primary" />
          <p className="text-xs font-semibold">Advertise on this page</p>
          <p className="text-[11px] text-muted-foreground">
            Reach thousands of property seekers each month.
          </p>
          <Link to="/advertise">
            <Button variant="outline" size="sm" className="h-8 text-xs w-full">
              View Packages
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
